import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const CustomSelectDropdown = ({
    options = [],
    value = null,
    onChange,
    onMenuOpen,
    onMenuClose,
    getOptionLabel = (option) => (option?.label || option?.name || String(option)),
    getOptionValue = (option) => (option?.id || option?.value || option),
    placeholder = 'Sélectionner une option',
    className = '',
    disabled = false,
    openUp = false,
    renderOptionLabel = null,
    inputStyle = {},
    menuPortalTarget = null,
    menuPosition = 'absolute',
    styles = {},
    menuInline = false,
    hideDropdownIcon = false,
    renderOptionEnd = null,
    isOptionDisabled = () => false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const [menuCoordinates, setMenuCoordinates] = useState({
        top: 0,
        left: 0,
        width: 0,
    });


    useEffect(() => {
        const updateMenuCoordinates = () => {
            if (!dropdownRef.current || !menuPortalTarget) return;
            const rect = dropdownRef.current.getBoundingClientRect();
            const gap = 4;
            const scrollX = menuPosition === 'fixed' ? 0 : window.pageXOffset;
            const scrollY = menuPosition === 'fixed' ? 0 : window.pageYOffset;
            setMenuCoordinates({
                top: (openUp ? rect.top - gap : rect.bottom + gap) + scrollY,
                left: rect.left + scrollX,
                width: rect.width,
            });
        };

        const handleClickOutside = (event) => {
            const clickedInsideTrigger = dropdownRef.current?.contains(event.target);
            const clickedInsideMenu = menuRef.current?.contains(event.target);
            if (!clickedInsideTrigger && !clickedInsideMenu) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            updateMenuCoordinates();
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('resize', updateMenuCoordinates);
            window.addEventListener('scroll', updateMenuCoordinates, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', updateMenuCoordinates);
            window.removeEventListener('scroll', updateMenuCoordinates, true);
        };
    }, [isOpen, menuPortalTarget, menuPosition, openUp]);

    useEffect(() => {
        if (isOpen) {
            onMenuOpen && onMenuOpen();
        } else {
            onMenuClose && onMenuClose();
        }
    }, [isOpen, onMenuOpen, onMenuClose]);

    const handleOptionClick = (option) => {
        if (isOptionDisabled(option)) return;
        if (onChange) {
            onChange(option);
        }
        setIsOpen(false);
    };

    const selectedOption = options.find(
        (option) => getOptionValue(option) === getOptionValue(value)
    );

    const hasSelection = !!selectedOption;
    const displayColor = hasSelection ? '#000' : '#9ca3af';

    const menuPortalStyle = typeof styles?.menuPortal === 'function'
        ? styles.menuPortal({ zIndex: 1000 })
        : (styles?.menuPortal || {});

    const menuElement = (
        <div
            ref={menuRef}
            style={menuInline ? {
                position: 'relative',
                marginTop: '4px',
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxHeight: '200px',
                overflowY: 'auto',
            } : menuPortalTarget ? {
                position: menuPosition,
                top: menuCoordinates.top,
                left: menuCoordinates.left,
                width: menuCoordinates.width,
                transform: openUp ? 'translateY(-100%)' : 'none',
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxHeight: '200px',
                overflowY: 'auto',
                ...menuPortalStyle,
            } : {
                position: 'absolute',
                ...(openUp ? {
                    bottom: '100%',
                    marginBottom: '4px',
                } : {
                    top: '100%',
                    marginTop: '4px',
                }),
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxHeight: '200px',
                overflowY: 'auto',
            }}
        >
            {options.map((option, index) => {
                const isSelected = getOptionValue(option) === getOptionValue(value);
                const isDisabled = isOptionDisabled(option);
                return (
                    <div
                        key={index}
                        onClick={() => handleOptionClick(option)}
                        style={{
                            padding: '10px 16px',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            backgroundColor: isSelected
                                ? '#f3f4f6'
                                : 'transparent',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '14px',
                            borderRadius: isSelected ? '4px' : '0',
                        }}
                        onMouseEnter={(e) => {
                            if (!isSelected && !isDisabled) {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isSelected && !isDisabled) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }
                        }}
                    >
                        <span style={{ flex: 1, minWidth: 0, opacity: isDisabled ? 1: 1 }}>
                            {renderOptionLabel ? renderOptionLabel(option, false) : ( // mode "liste déroulante"
                                option?.isCustom ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <img src="/Images/perso-icon.png" alt="Personnalisé" style={{ width: '16px', height: '16px' }} />
                                        <span>{getOptionLabel(option)}</span>
                                    </div>
                                ) : (
                                    getOptionLabel(option)
                                )
                            )}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            {typeof renderOptionEnd === 'function' ? renderOptionEnd(option) : null}
                            {isSelected && (
                                <img
                                    src="/Images/icon-check.png"
                                    alt="check"
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                    }}
                                />
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={className}
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.375rem 0.75rem',
                    paddingRight: hideDropdownIcon ? '0.75rem' : '2.5rem',
                    backgroundColor: disabled ? '#e5e7eb' : '#f3f3f5',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    minHeight: '38px',
                    fontSize: '14px',
                    opacity: disabled ? 0.6 : 0.6,
                    ...inputStyle,
                }}
            >
                <span
                    style={{
                        color: displayColor,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                    }}
                >
                    {value?.isCustom && (
                        <img
                            src="/Images/perso-icon.png"
                            alt="Personnalisé"
                            style={{ width: '16px', height: '16px', flexShrink: 0 }}
                        />
                    )}
                    {hasSelection
                        ? (
                            renderOptionLabel
                                ? renderOptionLabel(selectedOption, true)
                                : getOptionLabel(selectedOption)
                        )
                        : placeholder}
                </span>
                {!hideDropdownIcon && (
                    <img
                        src="/Images/arrow-icon.png"
                        alt="arrow"
                        style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: isOpen
                                ? 'translateY(-50%) rotate(180deg)'
                                : 'translateY(-50%)',
                            transition: 'transform 0.2s',
                            width: '12px',
                            height: '12px',
                        }}
                    />
                )}
            </div>
            {isOpen && !disabled && (
                (!menuInline && menuPortalTarget)
                    ? createPortal(menuElement, menuPortalTarget)
                    : menuElement
            )}
        </div>
    );
};

export default CustomSelectDropdown;